<template>
  <Form
    ref="signUpForm"
    autocomplete="off"
    autocorrect="off"
    autocapitalize="none"
    spellcheck="false"
    novalidate
    :class="{ desktop: $isDesktop }"
    :validation-schema="validationSchema"
    @submit="signUp"
  >
    <social-login />

    <div class="divider">
      <hr />
      <translate>or</translate>
      <hr />
    </div>

    <section class="new-email-account">
      <translate tag="p" class="create-account-intro">
        Create an account from your email address.
      </translate>

      <span class="new-account">
        <base-input
          v-model="user.email"
          autocomplete="email"
          name="email"
          required="true"
          type="email"
          validation="email"
          :validation-errors="validationErrors.email"
          @input="updateEmail($event)"
        >
          <template #label>
            <translate>Email address</translate>
          </template>
          <template #errorMessage="{ failedRules }">
            <!-- Show server validation errors first if they exist -->
            <span
              v-if="validationErrors.email && validationErrors.email.length"
              >{{ validationErrors.email[0] }}</span
            >
            <!-- Fall back to client-side validation messages -->
            <translate v-else-if="failedRules.includes('required')"
              >Please enter your email address</translate
            >
            <translate v-else-if="failedRules.includes('email')"
              >Please enter a valid email address</translate
            >
            <span v-else>{{ failedRules[0] }}</span>
          </template>
        </base-input>

        <base-input
          v-model="user.firstName"
          type="text"
          autocomplete="given-name"
          maxlength="30"
          name="firstName"
          required="true"
          validation="alpha"
          @input="clearFieldError('firstName')"
        >
          <template #label>
            <translate>First name</translate>
          </template>
          <template #errorMessage="{ failedRules }">
            <!-- Show server validation errors first if they exist -->
            <span
              v-if="
                validationErrors.firstName && validationErrors.firstName.length
              "
              >{{ validationErrors.firstName[0] }}</span
            >
            <!-- Fall back to client-side validation message -->
            <translate v-else>Please tell us your first name</translate>
          </template>
        </base-input>

        <base-input
          v-model="user.lastName"
          autocomplete="family-name"
          maxlength="30"
          name="lastName"
          type="text"
          required="true"
          validation="alpha"
          @input="clearFieldError('lastName')"
        >
          <template #label>
            <translate>Last name</translate>
          </template>
          <template #errorMessage="{ failedRules }">
            <!-- Show server validation errors first if they exist -->
            <span
              v-if="
                validationErrors.lastName && validationErrors.lastName.length
              "
              >{{ validationErrors.lastName[0] }}</span
            >
            <!-- Fall back to client-side validation message -->
            <translate v-else>Please tell us your last name</translate>
          </template>
        </base-input>
      </span>

      <base-password
        v-model="user.password"
        name="password"
        autocomplete="new-password"
        required="true"
        :minlength="minimumPasswordLength"
        :maxlength="maximumPasswordLength"
        @input="updatePassword($event)"
      >
        <template #label>
          <translate>Password</translate>
        </template>
        <template #helpText>
          <translate
            :tt="{ minimumPasswordLength, suggestedMinimumPasswordLength }"
          >
            Password is case sensitive: must be at least {minimumPasswordLength}
            letters ({suggestedMinimumPasswordLength} recommended)
          </translate>
        </template>
      </base-password>

      <br />
      <p>
        <i18n-t
          keypath="By clicking Sign up, you are indicating that you have read and agree to the {termsOfUse} and {privacyPolicy}."
        >
          <template #termsOfUse>
            <nuxt-link :to="$puLocalePath({ path: '/terms/' })" target="_blank">
              <translate>terms of use</translate>
            </nuxt-link>
          </template>

          <template #privacyPolicy>
            <nuxt-link
              :to="$puLocalePath({ path: '/privacy/' })"
              target="_blank"
            >
              <translate>privacy policy</translate>
            </nuxt-link>
          </template>
        </i18n-t>
      </p>
      <p>
        <i18n-t
          keypath="If you have any trouble creating your account please {contact}."
          formatter
        >
          <template #contact>
            <translate
              tag="a"
              :href="`https://www.pitchup.com/${
                $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
              }about/contact/`"
              target="_blank"
            >
              contact us
            </translate>
          </template>
        </i18n-t>
      </p>
      <div class="button-wrapper">
        <base-submit-button class="primary asblock" :submitting="isSubmitting">
          <translate>Sign up</translate>
          <template #submittingText>
            <translate>Submitting</translate>
          </template>
        </base-submit-button>
      </div>
    </section>
  </Form>
</template>

<script lang="ts">
import { getAuthService } from '../../auth/service'
import {
  Component,
  Vue,
  Watch,
  toNative,
  Setup,
} from '~/utility/pu-class-decorator'
import { getSignUpMachine } from '../inversify.config'
import { AuthMachineInterpreter } from '../../auth/authMachine'
import { SignUpMachineInterpreter } from '../machine'
import { toTypedSchema } from '@vee-validate/zod'
import { Form } from 'vee-validate'
import * as z from 'zod'

@Component({
  components: { Form },
})
class SignUpForm extends Vue {
  @Setup(() => {
    const route = useRoute()
    return getSignUpMachine((route.params.lang as string) || 'en-gb')
  })
  signUpMachine: ReturnType<typeof getSignUpMachine>

  minimumPasswordLength = 6
  maximumPasswordLength = 128
  suggestedMinimumPasswordLength = 8

  user = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  }

  validationErrors: Record<string, string[]> = {}

  authMachine: AuthMachineInterpreter

  validationSchema = toTypedSchema(
    z.object({
      email: z
        .string({ required_error: 'required' })
        .nonempty({ message: 'required' })
        .email({ message: 'email' }),
      password: z
        .string({ required_error: 'required' })
        .nonempty({ message: 'required' })
        .min(this.minimumPasswordLength, { message: 'min' })
        .max(this.maximumPasswordLength, { message: 'max' }),

      firstName: z
        .string({ required_error: 'required' })
        .nonempty({ message: 'required' }),
      lastName: z
        .string({ required_error: 'required' })
        .nonempty({ message: 'required' }),
    }),
  )

  mounted() {
    this.authMachine = getAuthService()
    this.signUpMachine?.onTransition(this.signUpChangeListener).start()
    this.authMachine.onTransition(this.authChangeListener)
  }

  destroyed() {
    this.signUpMachine.off(this.signUpChangeListener).stop()
    this.authMachine.off(this.authChangeListener)
  }

  get isSubmitting() {
    return this.signUpMachine.state?.matches('submitting')
  }

  private authChangeListener(state: AuthMachineInterpreter['state']) {
    if (state.matches('loggedIn')) {
      void this.$router.push(this.$puLocalePath({ name: 'account' }))
    }
  }

  private signUpChangeListener(state: SignUpMachineInterpreter['state']) {
    if (state.changed) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type XState context.validationErrors properly
      this.validationErrors = state.context.validationErrors as any

      if (state.matches('created')) {
        // Show success message
        const successMessage =
          state.context.successMessage ||
          'Please check your email to verify your account.'
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type Nuxt app plugins
        const { $snackbar } = useNuxtApp() as any
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Snackbar plugin
        $snackbar.add({
          type: 'success',
          text: successMessage,
          duration: 10000, // Show for 10 seconds
        })

        // Check if user is authenticated (auto-login flow)
        if (state.context.authenticated) {
          // User is auto-logged in, do a full page refresh to sync auth state
          setTimeout(() => {
            const redirectUrl = state.context.redirectUrl || '/account/'
            const localizedUrl = this.$puLocalePath(redirectUrl)
            window.location.href = localizedUrl
          }, 1000)
        } else {
          // User needs to verify email, redirect to login page
          setTimeout(() => {
            void this.$router.push(this.$puLocalePath('/accounts/login/'))
          }, 3000)
        }
      }

      // Handle general errors (500, network errors, etc.)
      if (
        state.matches('idle.errors') &&
        state.event.type === 'error.platform.createUser'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type XState event.data properly
        const error = (state.event as any).data
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type XState event.data properly
        if (error && !error.validationErrors) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type Nuxt app plugins
          const { $snackbar } = useNuxtApp() as any
          /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Snackbar plugin and error.message from XState */
          $snackbar.add({
            type: 'error',
            text:
              error.message || this.$t('Something went wrong, try again later'),
            duration: 8000,
          })
          /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
        }
      }
    }
  }

  @Watch('validationErrors')
  onErrorsChanged(errors: Record<string, string[]>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type vee-validate Form ref properly
    ;(this.$refs.signUpForm as any).setErrors(errors)
  }

  updateEmail(value: string) {
    // Clear server validation errors when user starts typing
    this.validationErrors = {}
    this.authMachine.send('CHANGE_EMAIL', { value })
  }

  updatePassword(value: string) {
    this.authMachine.send('CHANGE_PASSWORD', { value })
  }

  clearFieldError(fieldName: string) {
    // Clear server validation errors for specific field when user starts typing
    if (this.validationErrors[fieldName]) {
      this.validationErrors = {
        ...this.validationErrors,
        [fieldName]: [],
      }
    }
  }

  signUp(values?: Record<string, any>): Promise<void> {
    // Use form values if provided by vee-validate, otherwise use this.user
    const formData = values || this.user
    this.signUpMachine.send('SIGN_UP', { data: formData })
    return Promise.resolve()
  }
}

export default toNative(SignUpForm)
</script>

<style scoped>
form {
  background: var(--colour-page-background);
  padding-bottom: 6em;
}

.divider {
  margin: 1em 4vw;
  color: var(--colour-dark-blue);
}

.divider hr {
  background-color: #a2afbb;
}

.new-email-account {
  background: white;
  border-radius: 0.5em;
  margin: auto 3vw;
  padding: 0.5em 4vw 0.85em 5vw;
}

p {
  font-size: var(--body);
}

.create-account-intro {
  margin: 0.75em 0 1.2em;
  font-weight: bold;
  font-size: inherit;
  color: var(--colour-dark-blue);
}

.new-account :deep(label) {
  margin-bottom: 0.75em;
  display: block;
}

/* desktop & tablet */
form.desktop {
  border: 4px solid var(--colour-page-background);
  border-radius: 1em;
  background: none;
  padding: 0;
}

.desktop .divider {
  margin: 1em 1.5em;
}

.desktop .new-email-account {
  margin: auto;
  padding: 0 1.5em 1.5em;
}
</style>
